import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { makeStyles, useTheme } from '@mui/styles'
import Tooltip from '@mui/material/Tooltip'
import IconDelete from '@assets/images/icons/trashcan.svg'
import IconFavorite from '@assets/images/icons/favorite.svg'
import IconNoFavorite from '@assets/images/icons/no_favorite.svg'
import IconPlus from '@assets/images/icon_plus.svg'
import IconAttachment from '@assets/images/icons/attachment_vertical.svg'
import IconSignaturePen from '@assets/images/icons/signature_pen.svg'

import { SquareIconButton } from '@/modules/base/components/buttons/index'
import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'
import PropTypes from 'prop-types'
import PlaceHolder from './placeholder'
import orgamax from 'ampersand-app'
import lang from '@/lang'

import { FILE_TYPE, mimeTypes, experimentalFeatures } from '@/modules/base/util/constants'
// FLAG:useDocuAi — analysis-state tile badge (gated below).
import IconScanDocument from '@assets/images/icons/ocr.svg'
// FLAG:useDocuAi — the AI sparkle: badge icon while analysing, small static mark when done.
import IconAi from '@assets/images/icons/ai-sparkle.svg'
// FLAG:useDocuAi — the two terminal-verdict icons. Deliberately NOT IconScanDocument: that is
// the "Zu prüfen" glyph, and reusing it here made the two most opposite states differ only by
// colour. A magnifier-with-check says "searched/indexed"; a crossed-out eye says
// "unreadable" — each states its own outcome instead of borrowing the review one.
import IconMagnifierCheck from '@assets/images/icons/magnifier_check.svg'
import IconInvisible from '@assets/images/icons/invisible.svg'
import { getInvoiceAnalysisState, getInvoiceAnalysisLabel, hasLegacyOcrAnalysis, hasDocuAiAnalysis, hasXmlInvoiceAnalysis, INVOICE_ANALYSIS_STATE } from '@/modules/document/utils/invoice-analysis-state'
// FLAG:useDocuAi — makes the time-based age limit effective (see its rationale).
import { useAnalysisBlinkCap } from '@/modules/document/utils/use-analysis-blink-cap'
import AssignedBadge from './assigned-badge'
import { useDrag, useDrop } from 'react-dnd'
import clsx from 'clsx'
import CascadingContextMenu from '@/modules/base/components/cascading-context-menu/cascading-context-menu'
import { useDispatch } from 'react-redux'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'
import { Checkbox } from '@/modules/base/components/inputs/index'
import { revokeSelection, setItemSelection } from '@/redux/ducks/document/list'
import customDecodeUri from '@/utils/customDecodeUri'
import IconIsShared from '@assets/images/icons/is_shared.svg'
import IconWindows from '@assets/images/icons/windows.svg'
import { EXTERNAL_OWNER_SYSTEMS } from '@/modules/file/constants'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { setDocumentsFavorite } from '@/redux/ducks/lists/requests/documentList'
import UploadBadge from '@/modules/file/components/upload-badge'
import { useAppSelector } from '@/redux/store'
// FLAG:useDocuAi — child-faced search tile (matchedChild rows only).
import MatchedChildFilename from '@/modules/file/components/matched-child-info'

const mimetypesToFetch = [
  mimeTypes.MIMETYPE_JPG,
  mimeTypes.MIMETYPE_JPEG,
  mimeTypes.MIMETYPE_PNG,
]

const checkboxWidth = 18
const originalCheckboxWidth = 22

const useStyles = makeStyles(theme => ({
  dragWrapper: {
    height: '100%',
    padding: '0 10px 10px 15px',
  },
  topRight: {
    right: 15,
    top: 15,
    position: 'absolute',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 'calc(100% - 30px)',
  },
  favoriteWrapper: {
    cursor: 'pointer',
    width: 32,
    height: 32,
    padding: CssHelper.px(7, 7, 7),
    borderRadius: theme.borderRadius,
    border: theme.border,
    background: theme.backgroundColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    '&.isLoading': {
      cursor: 'wait',
      pointerEvents: 'none',
    },
  },
  checkbox: {
    transform: `scale(${checkboxWidth / originalCheckboxWidth})`,
  },
  addIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    fontWeight: 'bold',

    '& .SVGInline': {
      marginBottom: '.5rem',
    },
  },
  filename: {
    lineHeight: '1.17',
    letterSpacing: '0.12px',
    textAlign: 'center',
    fontSize: '12px',
    color: theme.text,
    margin: '15px 0',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'grid',
    gridTemplateColumns: (props) => props.allowDrop ? '1fr auto 1fr' : null,
  },
  fileLabel: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    cursor: 'default',
    margin: '0 6px',
  },
  item: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    background: theme.white,
    height: '100%',
    overflow: 'hidden',
    boxShadow: '0 5px 4px 0 rgba(0, 0, 0, 0.1)',
    aspectRatio: 0.71,
    margin: 'auto',
    width: 'unset !important',
    '& img': {
      objectFit: 'contain',
    },
    border: `1px solid ${theme.borderColor}`,
    '&:hover': {
      border: `1px solid ${theme.primaryDark}`,
    },
    '&.hover': {
      border: `1px solid ${theme.primaryDark}`,
    },
  },
  hover: {
    border: `1px solid ${theme.primaryDark}`,
  },
  imageWrapper: {
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  overlay: {
    opacity: 0,
    background: 'rgba(255,255,255,.75)',
    position: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    top: 0,
    transition: 'all 0.4s ease-in-out 0s',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    cursor: 'pointer',
    '&:hover': {
      opacity: 1,
    },
  },
  actionsWrapper: {
    position: 'absolute',
    bottom: 0,
    padding: '11px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    zIndex: 1,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& > *': {
      marginLeft: '10px',
    },
  },
  tags: {
    paddingLeft: 10,
    display: 'flex',
  },
  tagCircle: {
    backgroundColor: 'white',
    borderRadius: '50%',
    height: '1em',
    width: '1em',
    margin: 1,
    zIndex: 1,
  },
  border: {
    borderColor: theme.borderColor,
    borderWidth: 1,
    borderStyle: 'solid',
  },
  badgesWrapper: {
    display: 'flex',
    gap: 6,
  },
  // FLAG:useDocuAi — AI integrated into the single analysis badge.
  // While AI is working the badge icon IS the AI symbol and blinks.
  '@keyframes aiBlink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
  aiBlink: {
    animation: '$aiBlink 1.1s ease-in-out infinite',
  },
  // Relative wrapper so the small static AI sign can sit in the badge corner.
  badgeWithAi: {
    position: 'relative',
    display: 'inline-flex',
    lineHeight: 0,
  },
  // FLAG:useDocuAi — DocuAI abstract tooltip on the document preview.
  // MUST centre like `imageWrapper` does. The tooltip's anchor span is an extra box between
  // that flex container and the <img>, so with `display:block` the image lost the parent's
  // `align-items:center` and sat at the TOP of the tile — visibly misaligned against the
  // neighbouring tiles, and only for documents that HAVE a summary (i.e. the AI-processed
  // ones). Same flex centring here keeps wrapped and unwrapped thumbnails identical.
  summaryWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  summaryTooltip: {
    maxWidth: 360,
    fontSize: theme.fontSizes.small,
    lineHeight: 1.4,
    whiteSpace: 'pre-line',
  },
  // FLAG:useDocuAi — SquareIconButton shrinks its tooltip to `fontSizes.tooltip` (11px),
  // which is the ONLY place in the app that undercuts the global MuiTooltip size
  // (`smedium`). On a tile that made the badge tooltips visibly smaller than the filename
  // tooltip right below them. Restored here, per badge, rather than in SquareIconButton:
  // that component is used app-wide, so changing it would also move flag-OFF tooltips.
  // Applied ONLY to the three badges that are themselves flag-gated — the generated-files
  // badge renders for flag-OFF tenants too and keeps its current size.
  badgeTooltip: {
    fontSize: theme.fontSizes.smedium,
  },
  // Small "AI was involved" sparkle baked into the finished (yellow/green/red)
  // icon — bottom-left corner, transparent (no background), in the app blue so the
  // AI mark stands out against the status colour.
  aiCornerMark: {
    position: 'absolute',
    bottom: 1,
    left: 1,
    display: 'flex',
    pointerEvents: 'none',
    // The sparkle's paths use fill="currentColor"; drive it via `color` and also set
    // an explicit fill (a plain CSS rule beats the SVG presentation attribute — no
    // !important needed). NB: the app blue is `theme.blue` (#3264ff); it is NOT in
    // `theme.colors` (BASE_COLORS has white/yellow but no blue) — using
    // theme.colors.blue yields undefined and JSS drops the whole declaration.
    color: theme.blue,
    '& svg': { width: 14, height: 14, color: theme.blue },
    '& svg path': { fill: theme.blue },
  },
  attachmentButton: {
    background: theme.green,
    borderColor: theme.green,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.green),
    },
    '& svg': {
      fill: 'white !important',
    },
    '& svg path': {
      fill: 'white !important',
    },
  },
  generatedFilesButton: {
    backgroundColor: theme.green,
    borderColor: theme.green,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.green),
    },
    '& svg': {
      fill: 'white !important',
    },
    '& svg path': {
      fill: 'white !important',
    },
  },
  generatedFilesButtonReview: {
    backgroundColor: theme.colors.yellow,
    borderColor: theme.colors.yellow,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.colors.yellow),
    },
  },
  generatedFilesButtonAnalyzing: {
    backgroundColor: theme.primary,
    borderColor: theme.primary,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.primary),
    },
  },
  generatedFilesButtonInvalid: {
    backgroundColor: theme.error,
    borderColor: theme.error,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.error),
    },
  },
  // FLAG:useDocuAi — analysis-state tile badge (only rendered on the useDocuAi path).
  analysisStatusButton: {
    backgroundColor: theme.green,
    borderColor: theme.green,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.green),
    },
    '& svg': {
      fill: 'white !important',
    },
    '& svg path': {
      fill: 'white !important',
    },
  },
  analysisStatusButtonReview: {
    borderColor: theme.colors.yellow,
    backgroundColor: theme.colors.yellow,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.colors.yellow),
    },
  },
  analysisStatusButtonAnalyzing: {
    borderColor: theme.primary,
    backgroundColor: theme.primary,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.primary),
    },
  },
  // FLAG:useDocuAi — non-terminal transient retry (plan docuai-analysis-retrying): same AI
  // symbol, but calm (NO blink) and slightly dimmed — "delayed, will be retried
  // automatically" is a waiting state, not activity and not a failure (never orange/red).
  analysisStatusButtonRetrying: {
    borderColor: theme.primary,
    backgroundColor: theme.primary,
    opacity: 0.65,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.primary),
    },
  },
  analysisStatusButtonInvalid: {
    borderColor: theme.error,
    backgroundColor: theme.error,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.error),
    },
  },
  analysisStatusButtonFailed: {
    borderColor: theme.orange,
    backgroundColor: theme.orange,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.orange),
    },
  },
  aiFailureMark: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 15,
    height: 15,
    borderRadius: '50%',
    border: `2px solid ${theme.orange}`,
    backgroundColor: theme.white,
    color: theme.orange,
    fontSize: theme.fontSizes.tooltip,
    fontWeight: theme.fontWeights.bold,
    lineHeight: '11px',
    textAlign: 'center',
    pointerEvents: 'none',
  },
  // FLAG:useDocuAi — neutral "AI processed this document" badge for analysed
  // NON-invoices (PDF / office / picture): static AI sparkle, brand blue. The
  // blinking blue badge means "analysing"; this static one means "AI is done,
  // nothing to review".
  aiProcessedButton: {
    backgroundColor: theme.primary,
    borderColor: theme.primary,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.primary),
    },
    '& svg': {
      fill: 'white !important',
    },
    '& svg path': {
      fill: 'white !important',
    },
  },
  // FLAG:useDocuAi — terminal badge for a document that produces no analysis result:
  // an archive whose members are indexed, or a file type nothing can read (video, audio,
  // CAD, opaque binary). Deliberately NOT the brand-blue AI badge — no analysis ran, and
  // claiming one would be the same lie the missing badge used to tell in the other
  // direction. It is still a STATEMENT, not an absence: "we looked, there is nothing to
  // analyse here" is what distinguishes this from a lost analysis — which is exactly why
  // the former `greyStateColor` (#c6c6c6) was wrong: that is the theme's DISABLED value
  // (`disabledBorderColor`), so the badge read "locked/broken" instead of "done".
  //
  // The two readings get their own colour, both from lanes this badge row does not already
  // use (blue=analysing, yellow=review, green=done, orange=failed, red=invalid):
  //   contentIndexed → darkCyan, because "content searchable" is a delivered result.
  //   notApplicable  → black (the slate text colour), calm but properly legible on white.
  // `& svg circle` is listed alongside `path` because invisible.svg draws the pupil as a
  // <circle>; without it that one shape would keep the SVG default fill.
  analysisEligibilityButton: {
    '& svg': {
      fill: 'white !important',
    },
    '& svg path, & svg circle': {
      fill: 'white !important',
    },
  },
  analysisEligibilityButtonIndexed: {
    backgroundColor: theme.darkCyan,
    borderColor: theme.darkCyan,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.darkCyan),
    },
  },
  analysisEligibilityButtonNotApplicable: {
    backgroundColor: theme.black,
    borderColor: theme.black,
    '&:hover': {
      backgroundColor: CssHelper.important(theme.black),
    },
  },
  unseenBadge: {
    background: theme.primary,
    borderRadius: 17.5,
    height: 35,
    color: theme.white,
    border: theme.border,
    width: 66,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    '&.small': {
      height: '1em',
      width: '1em',
      border: 'none',
    },
  },
  leftBadgeWrapper: {
    display: 'flex',
  },
  analysis: {
    padding: 5,
    fontSize: 13,
    opacity: 0,
    '&:hover': {
      opacity: 1,
    },
  },
}))

const FavoriteBadge = ({ file }) => {
  const dispatch = useDispatch()
  const { updatedData } = useAppSelector(state => state.document.list)
  const classes = useStyles()

  const updatedFile = updatedData?.find(({ id }) => id === file.id)

  const isLoading = updatedFile?.updatingFields?.includes('isFavorite')

  return (
    <div className={clsx(classes.favoriteWrapper, { isLoading })}>
      <SvgIcon
        onClick={() => dispatch(setDocumentsFavorite([file], !file.isFavorite))}
        width="16px"
        svg={file.isFavorite ? IconFavorite : IconNoFavorite}
      />
    </div>
  )
}

const ThumbnailListItem = (props) => {
  const classes = useStyles(props)
  const [objectUrl, setObjectUrl] = useState(null)
  const {
    file,
    onSelectFile,
    onPreviewRequest,
    onDeleteFile,
    onAddTag,
    allowDrop,
    allowDrag,
    disableRightClickContextMenu,
    selectionEnabled,
    disableFavButton,
    disableDeleteButton,
    minSize,
    zoomLevel,
  } = props
  const theme = useTheme()
  const { mimetype, url, thumbnailUrl, name: fileName, metaData } = file
  const getContextMenu = useAppSelector(state => state.document.list.getContextMenu)
  const selectedItems = useAppSelector(state => state.document.list.selectedItems)
  const isSelected = useMemo(() => {
    return !!selectedItems.find(({ id }) => id === file.id)
  }, [selectedItems])
  const dispatch = useDispatch()

  const [collectedProps, drop] = allowDrop
    ? useDrop({
      accept: 'TAG',
      drop: (item) => {
        onAddTag && onAddTag({ fileId: file.id, tag: item.tag })
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    })
    : [null, null]

  useEffect(() => {
    if (thumbnailUrl || !mimetypesToFetch.includes(mimetype) || file.uploadToken || !url) {
      setObjectUrl(null)
      return
    }

    setObjectUrl(`${url}?token=${orgamax.user.token}`)
  }, [file.id, file.uploadToken, mimetype, thumbnailUrl, url])

  const { rights } = useCurrentModuleContext()

  const handleSelect = () => {
    onSelectFile && onSelectFile(file)
  }
  const handleContextMenu = useCallback((e) => {
    if (!isSelected) {
      dispatch(revokeSelection())
    }
  }, [dispatch, file, isSelected])

  const handlePreviewRequest = () => {
    onPreviewRequest && onPreviewRequest(file)
  }

  const handleDelete = (e) => {
    onDeleteFile && onDeleteFile(file)
    e.stopPropagation()
  }

  const onChangeSelection = (selected, _name, modifiers) => {
    dispatch(setItemSelection(file, selected, modifiers))
  }

  const deleteButton = (
    file.type === FILE_TYPE.DOCUMENT && !disableDeleteButton
    && <SquareIconButton
      icon={IconDelete}
      tooltip={lang.deleteButtonLabel}
      onClick={handleDelete} />
  )

  const sharedButton = (
    <SquareIconButton
      key='sharedButton'
      icon={IconIsShared}
      tooltip={lang.documents.isShared}
      onClick={() => {}} />
  )

  const desktopAssignedButton = (
    <SquareIconButton
      className={classes.attachmentButton}
      icon={IconWindows}
      iconColor={theme.white}
      tooltip={lang.documents.isAssignedToWindows}
      onClick={() => {}}
      key='assignedButton' />
  )

  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  // FLAG:useDocuAi — keep the additive selector/backstop completely inert for legacy tenants.
  // Besides avoiding the AI state calculation, this guarantees flag-OFF never allocates the
  // first-observed timestamp or starts the one-second render timer below.
  const invoiceAnalysisState = useDocuAiEnabled ? getInvoiceAnalysisState(file) : null
  // FLAG:useDocuAi — getInvoiceAnalysisState's age limit elapses over TIME but is evaluated during
  // rendering. The AI symbol blinks right here: without a tick, it stays in that state when the
  // terminal event is missing because nothing triggers another render. Rationale and interval
  // choice live in use-analysis-blink-cap.
  useAnalysisBlinkCap(
    !!useDocuAiEnabled && (
      invoiceAnalysisState === INVOICE_ANALYSIS_STATE.ANALYZING
      || invoiceAnalysisState === INVOICE_ANALYSIS_STATE.RETRYING
    ),
  )
  // FLAG:useDocuAi — the AI badge belongs to documents DocuAI actually analysed. A gini/natif
  // result from before the switch keeps the plain icon it always had (see hasDocuAiAnalysis).
  const hasDocuAiOcrAnalysis = hasLegacyOcrAnalysis(file) && hasDocuAiAnalysis(file)
  const hasXmlInvoiceArtifacts = useDocuAiEnabled && hasXmlInvoiceAnalysis(file)
  const shouldColorGeneratedFilesButton = useDocuAiEnabled && (hasXmlInvoiceArtifacts || invoiceAnalysisState)
  const hasInvoiceArtifacts = file.hasGeneratedFiles || hasXmlInvoiceArtifacts

  // FLAG:useDocuAi — while AI is working the single status badge shows the AI
  // symbol (blinking); when finished it shows the normal coloured icon with a
  // small static AI sign baked into the corner (see withAiMark below).
  const isAiAnalyzing = useDocuAiEnabled && invoiceAnalysisState === INVOICE_ANALYSIS_STATE.ANALYZING
  // FLAG:useDocuAi — transient retry (plan docuai-analysis-retrying): AI symbol stays, the
  // blink stops, the tooltip explains the automatic retry (Versuch n/m).
  const isAiRetrying = useDocuAiEnabled && invoiceAnalysisState === INVOICE_ANALYSIS_STATE.RETRYING
  const isAiFailed = useDocuAiEnabled && invoiceAnalysisState === INVOICE_ANALYSIS_STATE.FAILED
  const withAiMark = (badge, key) => {
    // Flag-OFF: identity passthrough so the base badge is pushed directly (own key kept),
    // leaving the flag-OFF DOM byte-identical to f2ac6a682f — no extra wrapper span.
    if (!useDocuAiEnabled) return badge
    return (
      <span className={classes.badgeWithAi} key={key}>
        {badge}
        {!isAiAnalyzing && (
          <span className={classes.aiCornerMark}><SvgIcon svg={IconAi} height='9px' width='9px' /></span>
        )}
      </span>
    )
  }
  const withAiFailureMark = (badge, key) => (
    <span className={classes.badgeWithAi} key={key}>
      {badge}
      <span className={classes.aiFailureMark} aria-hidden='true'>!</span>
    </span>
  )

  const generatedFilesButton = (
    <SquareIconButton
      className={clsx(classes.generatedFilesButton, {
        [classes.generatedFilesButtonAnalyzing]: shouldColorGeneratedFilesButton && (invoiceAnalysisState === INVOICE_ANALYSIS_STATE.ANALYZING || (hasXmlInvoiceArtifacts && !invoiceAnalysisState)),
        [classes.generatedFilesButtonReview]: shouldColorGeneratedFilesButton && invoiceAnalysisState === INVOICE_ANALYSIS_STATE.REVIEW,
        [classes.generatedFilesButtonInvalid]: shouldColorGeneratedFilesButton && invoiceAnalysisState === INVOICE_ANALYSIS_STATE.INVALID,
        [classes.aiBlink]: isAiAnalyzing,
      })}
      icon={isAiAnalyzing ? IconAi : IconAttachment}
      iconColor='white'
      customIconSize='20px'
      tooltip={lang.documents.hasGeneratedFiles}
      onClick={() => {}}
      key='generatedFilesButton' />
  )

  const signatureButton = (
    <SquareIconButton
      className={classes.attachmentButton}
      icon={IconSignaturePen}
      tooltip={lang.documents.hasSignature}
      onClick={() => {}}
      key='signatureButton' />
  )

  // FLAG:useDocuAi — analysis-state badge for legacy/OCR-analysed incoming invoices ("In Analyse" /
  // "Zu prüfen" / "E-Rechnung ungültig" …). Computed always (cheap, pure) but only pushed into the
  // badges on the useDocuAi path, so flag-OFF tiles are unchanged.
  const analysisStatusButton = (
    <SquareIconButton
      className={clsx(classes.analysisStatusButton, {
        [classes.analysisStatusButtonAnalyzing]: invoiceAnalysisState === INVOICE_ANALYSIS_STATE.ANALYZING,
        [classes.analysisStatusButtonRetrying]: invoiceAnalysisState === INVOICE_ANALYSIS_STATE.RETRYING,
        [classes.analysisStatusButtonReview]: invoiceAnalysisState === INVOICE_ANALYSIS_STATE.REVIEW,
        [classes.analysisStatusButtonInvalid]: invoiceAnalysisState === INVOICE_ANALYSIS_STATE.INVALID,
        [classes.analysisStatusButtonFailed]: invoiceAnalysisState === INVOICE_ANALYSIS_STATE.FAILED,
        [classes.aiBlink]: isAiAnalyzing,
      })}
      classes={{ tooltip: classes.badgeTooltip }}
      icon={(isAiAnalyzing || isAiRetrying || isAiFailed) ? IconAi : IconScanDocument}
      iconColor='white'
      customIconSize='20px'
      tooltip={isAiRetrying
        ? lang.documents.invoiceAnalysisRetryingTooltip(file.metaData?.analysisRetrying)
        : getInvoiceAnalysisLabel(invoiceAnalysisState)}
      onClick={() => {}}
      key='analysisStatusButton' />
  )

  // FLAG:useDocuAi — terminal badge for the document classes that never produce an analysis
  // result. One badge, two readings, taken from the DMS verdict:
  //   contentIndexed — an archive; its members are searchable (magnifier + check, darkCyan).
  //   notApplicable  — video/audio/CAD/binary; nothing can read it (crossed eye, slate).
  // Neither shares the "Zu prüfen" glyph any more — see the icon imports.
  const isContentIndexed = invoiceAnalysisState === INVOICE_ANALYSIS_STATE.CONTENT_INDEXED
  const analysisEligibilityButton = (
    <SquareIconButton
      className={clsx(classes.analysisEligibilityButton, isContentIndexed
        ? classes.analysisEligibilityButtonIndexed
        : classes.analysisEligibilityButtonNotApplicable)}
      classes={{ tooltip: classes.badgeTooltip }}
      icon={isContentIndexed ? IconMagnifierCheck : IconInvisible}
      iconColor='white'
      customIconSize='18px'
      tooltip={getInvoiceAnalysisLabel(invoiceAnalysisState)}
      onClick={() => {}}
      key='analysisEligibilityButton' />
  )

  // FLAG:useDocuAi — static AI badge for an analysed NON-invoice (no review state).
  const aiProcessedButton = (
    <SquareIconButton
      className={classes.aiProcessedButton}
      classes={{ tooltip: classes.badgeTooltip }}
      icon={IconAi}
      iconColor='white'
      customIconSize='18px'
      tooltip={lang.documents.aiProcessed}
      onClick={() => {}}
      key='aiProcessedButton' />
  )

  // FLAG:useDocuAi — the search match came from a child document (attachment/report/
  // validation report/XML): face the tile with the CHILD (its DMS-streamed thumbnail +
  // name + parent hint). Row identity/click/selection stay the parent's file.
  // Server only attaches matchedChild for flagged tenants; the flag check here is
  // belt-and-braces so flag-OFF tiles are unchanged by construction.
  const matchedChild = useDocuAiEnabled ? file.matchedChild : undefined

  const placeHolder = useMemo(() => {
    const size = props.size ?? zoomLevel
    if (matchedChild) {
      return (
        <PlaceHolder name={matchedChild.childName} size={size} mimetype={mimetype}
          minioId={matchedChild.childMinioId} thumbnailUrl={matchedChild.thumbnailUrl} objectUrl={null} />
      )
    }
    return (
      <PlaceHolder name={file.name} size={size} mimetype={mimetype}
        minioId={file.minioId} thumbnailUrl={thumbnailUrl} objectUrl={objectUrl} rotate={metaData?.rotate} />
    )
  }, [file.id, file.name, file.minioId, objectUrl, mimetype, thumbnailUrl, metaData?.rotate, zoomLevel, matchedChild])

  const allMenuItems = useCallback((getContextMenu(rights, file)), [file.id, file.isFavorite, selectedItems.length, file.uploadToken, file.metaData?.ocr])
  const menuItems = allMenuItems.filter(item => item.showInContextMenu !== false)

  const [, drag] = allowDrag
    ? useDrag({
      item: { file },
      type: 'FILE',
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    })
    : [null, null]

  const badges = useMemo(() => {
    const iconButtons = []
    if (file.externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop) iconButtons.push(desktopAssignedButton)
    if (file.isAssigned || file.hasTodoLinked || file.signatureCount > 0) iconButtons.push(<AssignedBadge file={file} key={'assignedBadge'} />)
    if (isAiFailed) iconButtons.push(withAiFailureMark(analysisStatusButton, 'analysisFailed'))
    else if (hasInvoiceArtifacts) iconButtons.push(withAiMark(generatedFilesButton, 'genfiles'))
    // FLAG:useDocuAi — analysis-state badge for legacy/OCR invoices, OR the blinking
    // AI badge for any document currently being analysed (no ocr/artifacts yet).
    else if (useDocuAiEnabled && (hasDocuAiOcrAnalysis || invoiceAnalysisState === INVOICE_ANALYSIS_STATE.ANALYZING || invoiceAnalysisState === INVOICE_ANALYSIS_STATE.RETRYING)) {
      iconButtons.push(withAiMark(analysisStatusButton, 'analysisStatus'))
    // FLAG:useDocuAi — no analysis result will ever come for this document (archive, video,
    // audio, CAD, binary). Ranked ABOVE the static AI badge on purpose: an archive also
    // carries aiProcessed when DocuAI/Tika produced its text, and the content-indexed state is the
    // more specific and more useful answer than "KI-analysiert" for a container.
    } else if (useDocuAiEnabled && (isContentIndexed
      || invoiceAnalysisState === INVOICE_ANALYSIS_STATE.NOT_APPLICABLE)) {
      iconButtons.push(analysisEligibilityButton)
    // Analysed NON-invoice (PDF/office/picture): keep a static AI badge so the user
    // sees AI was involved, even though there's nothing to review.
    } else if (useDocuAiEnabled && file.metaData?.aiProcessed) {
      iconButtons.push(aiProcessedButton)
    }
    if (file.signatureCount > 0) iconButtons.push(signatureButton)
    if (file.isShared) iconButtons.push(sharedButton)

    if (iconButtons.length > 0) return <div className={classes.badgesWrapper}>{iconButtons}</div>
    return <div></div>
  }, [
    file.isAssigned, file.isShared, file.externalOwnerSystem, file.hasTodoLinked, hasInvoiceArtifacts,
    useDocuAiEnabled, hasDocuAiOcrAnalysis, invoiceAnalysisState, shouldColorGeneratedFilesButton, file.metaData?.aiProcessed,
    // Retry-state tooltip content (Versuch n/m) changes with every whisper.
    file.metaData?.analysisRetrying,
  ])

  const small = +minSize.split('px')[0] < 225
  const unseenBadge = file.unseen && file.type === FILE_TYPE.DOCUMENT
    ? small
      ? <Tooltip title={lang.generics.new}><span className={clsx(classes.unseenBadge, { small })}></span></Tooltip>
      : <span className={classes.unseenBadge}>{lang.generics.new}</span>
    : null
  const ocrSource
    = metaData?.ocr?.type === 'natif'
      ? 'n'
      : metaData?.ocr?.type === 'gini'
        ? 'g'
        : metaData?.ocr?.type === 'xmlExtraction' ? 'z' : '?'

  return (
    <div
      className={classes.dragWrapper}
      ref={drag}
      onContextMenu={handleContextMenu}
    >
      <CascadingContextMenu
        disabled={disableRightClickContextMenu}
        menuItems={menuItems}
        height={'100%'}
      >
        <div ref={drop} className={clsx(classes.item, collectedProps?.isOver && classes.hover)}>
          <div className={classes.topRight}>
            <span className={classes.leftBadgeWrapper}>
              {disableFavButton
                ? null
                : <FavoriteBadge file={file} />}
              {unseenBadge}
            </span>
            {selectionEnabled && <Checkbox dataQsId='thumbnailListItem-fileSelected' name='fileSelected' customVisualClass={classes.checkbox} checked={isSelected} onChange={onChangeSelection} />}
          </div>
          <UploadBadge file={file} />
          <div className={classes.imageWrapper} onClick={handlePreviewRequest}>
            {onSelectFile && <div className={classes.overlay} onClick={handleSelect}>
              <span className={classes.addIcon}>
                <SvgIcon svg={IconPlus} height="25px" width="25px" />
                {lang.generics.choose}
              </span>
            </div>}
            <div className={classes.actionsWrapper}>
              {badges}
              <div className={classes.actions}>
                {!small && metaData?.ocr ? <div className={classes.analysis}>{ocrSource}</div> : null}
                {!file.isAssigned && onDeleteFile && deleteButton}
              </div>
            </div>
            {/* FLAG:useDocuAi — DocuAI abstract as a tooltip on the document preview. */}
            {useDocuAiEnabled && metaData?.summary
              ? <Tooltip title={metaData.summary} enterDelay={400} classes={{ tooltip: classes.summaryTooltip }}>
                <span className={classes.summaryWrap}>{placeHolder}</span>
              </Tooltip>
              : placeHolder}
          </div>
        </div>
        <div className={classes.filename}>
          { allowDrop && <div className={classes.tags}>
            {
              file?.tags?.map((tag, i) =>
                (<Tooltip key={`tooltip#${i}`} title={tag?.label}>
                  <div className={clsx(classes.tagCircle, !tag?.backgroundColor && classes.border)} style={{ backgroundColor: tag?.backgroundColor ?? theme.newTagColor }} />
                </Tooltip>),
              )
            }
          </div> }
          {matchedChild
            ? <MatchedChildFilename matchedChild={matchedChild} parentName={fileName} />
            : <Tooltip title={customDecodeUri(fileName)}>
              <span className={classes.fileLabel}>
                {customDecodeUri(fileName)}
              </span>
            </Tooltip>}
          <div />
        </div>
      </CascadingContextMenu>
    </div>
  )
}

ThumbnailListItem.propTypes = {
  file: PropTypes.object.isRequired,
  allowDrop: PropTypes.bool,
  allowDrag: PropTypes.bool,
  onSelectFile: PropTypes.func,
  onPreviewRequest: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func,
  onAddTag: PropTypes.func,
  selectionEnabled: PropTypes.bool,
  disableFavButton: PropTypes.bool,
  disableDeleteButton: PropTypes.bool,
  zoomLevel: PropTypes.number,
  minSize: PropTypes.string,
  size: PropTypes.string,
}

ThumbnailListItem.defaultProps = {
  allowDrop: false,
  allowDrag: false,
  disableFavButton: false,
  disableDeleteButton: false,
}

export default React.memo(ThumbnailListItem)
